import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Geofence } from '../../types/geofence';
import { geofenceService } from '../../services/geofenceService';

interface GeofenceState {
  /**
   * The fence assigned to the planner's selected drone. null means either no
   * drone is selected or the drone has no fence — `status` disambiguates.
   */
  geofence: Geofence | null;
  /** Drone the loaded fence belongs to, so a stale response can be ignored. */
  droneId: string | null;
  status: 'idle' | 'loading' | 'loaded' | 'error';
  error: string | null;
}

const initialState: GeofenceState = {
  geofence: null,
  droneId: null,
  status: 'idle',
  error: null,
};

/**
 * Load a drone's geofence for planner feedback (issue #451). Resolves to null
 * when the drone has no fence — an ordinary outcome, not a failure.
 */
export const fetchDroneGeofence = createAsyncThunk<
  { droneId: string; geofence: Geofence | null },
  string,
  { rejectValue: string }
>('geofence/fetchDroneGeofence', async (droneId, { rejectWithValue }) => {
  try {
    const geofence = await geofenceService.getDroneGeofence(droneId);
    return { droneId, geofence };
  } catch {
    return rejectWithValue(
      'Could not load this drone’s geofence — waypoints will not be checked until it loads.'
    );
  }
});

const geofenceSlice = createSlice({
  name: 'geofence',
  initialState,
  reducers: {
    /** Drop the loaded fence — the planner deselected its drone. */
    clearDroneGeofence(state) {
      state.geofence = null;
      state.droneId = null;
      state.status = 'idle';
      state.error = null;
    },
    /**
     * Replace the fence directly. Used by tests and by any future push of a
     * changed fence; the thunk is the normal path.
     */
    droneGeofenceLoaded(
      state,
      action: PayloadAction<{ droneId: string; geofence: Geofence | null }>
    ) {
      state.droneId = action.payload.droneId;
      state.geofence = action.payload.geofence;
      state.status = 'loaded';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDroneGeofence.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        // Drop the previous drone's fence immediately: showing one drone's
        // boundary while another is selected would be worse than showing none.
        if (state.droneId !== action.meta.arg) {
          state.geofence = null;
          state.droneId = action.meta.arg;
        }
      })
      .addCase(fetchDroneGeofence.fulfilled, (state, action) => {
        state.droneId = action.payload.droneId;
        state.geofence = action.payload.geofence;
        state.status = 'loaded';
        state.error = null;
      })
      .addCase(fetchDroneGeofence.rejected, (state, action) => {
        state.status = 'error';
        state.geofence = null;
        // Loud, not silent: an absent fence and an unloadable one look the same
        // on the map, so the operator has to be told which this is.
        state.error = action.payload ?? 'Could not load the geofence.';
      });
  },
});

export const { clearDroneGeofence, droneGeofenceLoaded } = geofenceSlice.actions;
export default geofenceSlice.reducer;
